import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import superAdminAuthService from '../../services/superAdminAuth.service';
import './SuperAdminAuth.scss';

const SuperAdminResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [tokenState, setTokenState] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const [formData, setFormData] = useState({ password: '', confirm_password: '' });
  const [formErrors, setFormErrors] = useState({ password: '', confirm_password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setTokenState('invalid');
      return;
    }
    superAdminAuthService.validateResetToken(token)
      .then(() => setTokenState('valid'))
      .catch(() => setTokenState('invalid'));
  }, [token]);

  const validate = (): boolean => {
    const errors = { password: '', confirm_password: '' };
    if (!formData.password) {
      errors.password = 'Le mot de passe est obligatoire';
    } else if (formData.password.length < 8) {
      errors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre';
    }
    if (!formData.confirm_password) {
      errors.confirm_password = 'La confirmation est obligatoire';
    } else if (formData.password !== formData.confirm_password) {
      errors.confirm_password = 'Les mots de passe ne correspondent pas';
    }
    setFormErrors(errors);
    return !errors.password && !errors.confirm_password;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setError('');
    try {
      await superAdminAuthService.resetPassword({ token, ...formData });
      navigate('/super-admin/login', { state: { passwordReset: true } });
    } catch (err: any) {
      const msg = err.response?.data?.error?.message;
      setError(msg || 'Une erreur est survenue. Le lien est peut-être expiré.');
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenState === 'loading') {
    return (
      <div className="super-admin-auth">
        <div className="super-admin-auth__card">
          <p className="super-admin-auth__eyebrow">ZEEDCOM Control</p>
          <p className="super-admin-auth__subtitle">Vérification du lien…</p>
        </div>
      </div>
    );
  }

  if (tokenState === 'invalid') {
    return (
      <div className="super-admin-auth">
        <div className="super-admin-auth__card">
          <p className="super-admin-auth__eyebrow">ZEEDCOM Control</p>
          <h1 className="super-admin-auth__title">Lien invalide</h1>
          <div className="super-admin-auth__alert super-admin-auth__alert--error">
            Ce lien est invalide ou a expiré. Demandez un nouveau lien.
          </div>
          <div className="super-admin-auth__footer">
            <Link to="/super-admin/forgot-password">Nouveau lien</Link>
            {' · '}
            <Link to="/super-admin/login">Connexion</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-auth">
      <div className="super-admin-auth__card">
        <p className="super-admin-auth__eyebrow">ZEEDCOM Control</p>
        <h1 className="super-admin-auth__title">Nouveau mot de passe</h1>
        <p className="super-admin-auth__subtitle">Choisissez un mot de passe sécurisé.</p>

        {error && <div className="super-admin-auth__alert super-admin-auth__alert--error">{error}</div>}

        <form className="super-admin-auth__form" onSubmit={handleSubmit}>
          <Input
            label="Nouveau mot de passe"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={formErrors.password}
            placeholder="Min. 8 caractères"
            required
            fullWidth
            autoComplete="new-password"
          />
          <Input
            label="Confirmer le mot de passe"
            type="password"
            name="confirm_password"
            value={formData.confirm_password}
            onChange={handleChange}
            error={formErrors.confirm_password}
            placeholder="Répétez le mot de passe"
            required
            fullWidth
            autoComplete="new-password"
          />
          <div className="super-admin-auth__actions">
            <Button type="submit" variant="primary" size="large" fullWidth loading={isLoading}>
              Réinitialiser
            </Button>
          </div>
        </form>

        <div className="super-admin-auth__footer">
          <Link to="/super-admin/login">Retour à la connexion</Link>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminResetPassword;
